import React, { useState, useCallback } from "react";
import { IoChevronDown, IoChevronUp, IoOpenOutline } from "react-icons/io5";


import toast from "react-hot-toast";

import axiosInstance from "../../../api/axios.instance";

import ToastWarning from "../../../toast/components/toastWarning/ToastWarning";

import styles from "./styles.module.scss";
import { modals } from "@mantine/modals";

// "percentage … for N sales": which sales these are, with what is due from each
const UnpaidSales = ({ userId }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const toggle = async () => {
    setOpen((x) => !x);
    if (rows) return;
    try {
      const { data } = await axiosInstance.get(`/users/${userId}/unpaid-sales`);
      setRows(data.apiData || []);
    } catch (e) {
      setRows([]);
      toast.error("Could not load the sales");
    }
  };
  const shown = (rows || []).filter((x) => x.due > 0 || x.share > 0);
  return (
    <div className={styles.salesBox}>
      <button type="button" className={styles.salesToggle} onClick={toggle}>
        {open ? <IoChevronUp /> : <IoChevronDown />}
        {open ? "Hide the sales" : "Show the sales"}
      </button>
      {open && !rows && <small className={styles.salesNote}>Loading…</small>}
      {open && rows && (
        <div className={styles.salesList}>
          {!shown.length && <small className={styles.salesNote}>No unpaid sales.</small>}
          {shown.map((x) => (
            <div key={`${x.id}-${x.role}`} className={styles.saleRow}>
              <div className={styles.saleMain}>
                <div className={styles.saleTitle} title={x.title}>
                  {x.videoId ? (
                    <a href={`/library/${x.videoId}`} target="_blank" rel="noopener noreferrer">
                      VB{x.videoId} <IoOpenOutline />
                    </a>
                  ) : null}{" "}
                  {x.title || x.report || "—"}
                </div>
                <small>
                  {new Date(x.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {x.company}
                  {x.count > 1 ? ` · ${x.count} sales` : ""} · sale {money(x.total)}
                  {x.role === "stringer" ? " · as stringer" : ""}
                  {x.ordinaryNews ? " · ordinary news" : ""}
                </small>
              </div>
              <div className={styles.saleMoney}>
                <b>{money(x.due)}</b>
                {x.due !== x.share && (
                  <small title="Part of this share covers the negative balance">
                    of {money(x.share)} · {x.coversNegativeBalance === "completely" ? "covers the balance" : "rest covers the balance"}
                  </small>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PaymentModal = ({ innerProps }) => {
  const [isRequested, setIsRequested] = useState(false);
  const [freeValue, setFreeValue] = useState("");

  const definePayloadForCommitRequest = useCallback(() => {
    const paymentVariants = [];

    let amountData = {};

    // Check if the user is an editor, copyright manager, or output editor
    const isSpecialPaymentRole = innerProps?.userId && 
      innerProps?.salaryData?.paymentVariants?.includes("extra");

    if (innerProps.hasOwnProperty("salaryData")) {
      paymentVariants.push(...innerProps?.salaryData?.paymentVariants);
      amountData = innerProps.salaryData.amount;
    }

    if (!!freeValue) {
      // For editors, copyright managers, and output editors, replace the amount with the free value
      if (isSpecialPaymentRole) {
        paymentVariants.push("extra");
        amountData = {
          total: +freeValue,
          extra: +freeValue,
        };
      } else {
        // For other roles, add the free value to the existing amount
        paymentVariants.push("extra");
        amountData = {
          ...amountData,
          total: amountData.hasOwnProperty("total")
            ? amountData.total + +freeValue
            : freeValue,
          extra: +freeValue,
        };
      }
    }

    return {
      paymentVariants,
      amountData,
    };
  }, [innerProps, freeValue]);

  const onMakePayment = useCallback(async () => {
    try {
      setIsRequested(true);
      let data;

      if (innerProps.companyId) {
        const res = await axiosInstance.post(
            `/payment/commit`,
            definePayloadForCommitRequest(),
            {
              params: {
                companyId: innerProps.companyId,
              },
            }
        );
        data = res.data;

      } else {
        const res = await axiosInstance.post(
            `/payment/commit`,
            definePayloadForCommitRequest(),
            {
              params: {
                userId: innerProps.userId
              },
            }
        );
        data = res.data;
      }


      switch (data.status) {
        case "warning":
          toast.custom(<ToastWarning text={data.message} />);
          break;
        case "success":
          modals.closeAll();
          if (innerProps.hasOwnProperty("onTrigger")) {
            innerProps.onTrigger();
          }
          toast.success(data.message);
          setFreeValue("");
          break;
      }
    } catch (err) {
      console.log(err);
      toast.error("Server side error");
    } finally {
      setIsRequested(false);
    }
  }, [
    setIsRequested,
    innerProps,
    setFreeValue,
    axiosInstance,
    definePayloadForCommitRequest,
  ]);

  const isSpecialPaymentRole = innerProps?.userId && innerProps?.salaryData?.paymentVariants?.includes("extra");
  const due = innerProps?.salaryData?.amount?.total ? Number(innerProps.salaryData.amount.total) : 0;
  const extra = Number(freeValue) || 0;
  // editors & co: the extra amount replaces the sum; others: it is added
  const total = extra ? (isSpecialPaymentRole ? extra : due + extra) : due;
  const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const toSubmitForm = useCallback(
    (event) => {
      event.preventDefault();
      if (total > 0 && !isRequested) onMakePayment();
    },
    [total, isRequested, onMakePayment]
  );

  return (
    <form className={styles.form} onSubmit={toSubmitForm}>
      <div className={styles.due}>
        <span>Due now</span>
        <b>{money(due)}</b>
        {(innerProps?.salaryData?.description || []).length > 0 && (
          <ul>
            {innerProps.salaryData.description.map((val) => (
              <li key={val}>{val}</li>
            ))}
          </ul>
        )}
        {!due && <small>Nothing is due right now. You can still pay an extra amount.</small>}
        {innerProps?.userId && (innerProps?.salaryData?.description || []).some((d) => /sales/.test(d)) && (
          <UnpaidSales userId={innerProps.userId} />
        )}
      </div>

      <label className={styles.extra}>
        {isSpecialPaymentRole ? "Amount to pay" : "Extra amount (optional)"}
        <span className={styles.field}>
          <i>$</i>
          <input
            inputMode="decimal"
            placeholder="0.00"
            disabled={isRequested}
            value={freeValue}
            onChange={(event) => setFreeValue(event.target.value.replace(/[^0-9.]/g, ""))}
          />
        </span>
        <small>
          {isSpecialPaymentRole
            ? "Replaces the amount above."
            : "Added to the amount due, e.g. a bonus. The total is paid in one payment."}
        </small>
      </label>

      <button type="submit" className={styles.pay} disabled={isRequested || total <= 0}>
        {isRequested ? "Paying…" : total > 0 ? `Pay ${money(total)}` : "Nothing to pay"}
      </button>
    </form>
  );
};

export default PaymentModal;
